import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useMutation } from "@tanstack/react-query"
import { Search, UserPlus, X } from "lucide-react"
import {
  searchHeroContacts, kontaktName, kontaktFirmaZusatz, type HeroContactHit,
} from "@/lib/heroSearch"
import { toast } from "@/components/portal/Toast"

/**
 * „Als Ansprechpartner:in anlegen" — für jemanden, der schreibt, aber kein
 * eigener Kunde ist.
 *
 * 🔴 ANLASS (16.08.2026, Mail #5941): Frau Beispielhof schreibt wegen einer Wartung
 * in einer Wohnung, die ihr nicht gehört. Bisher gab es nur zwei schlechte
 * Wege — sie als eigene Kundin anlegen (falsch: sie zahlt nichts und bekommt
 * keine Rechnung) oder gar nicht anlegen (dann fehlt beim nächsten Anruf jede
 * Spur). HERO kennt die richtige Form längst: der Kontakt hängt per
 * `parent_customer_id` unter dem Kunden und ist als Ansprechpartner markiert.
 *
 * Bewusst schmal gehalten: ein Kunde, ein Name, fertig. Wer mehr braucht —
 * Objekt, Vorgang, Termin — nimmt danach die normalen Wege. Ein Dialog, der
 * alles kann, wird für den häufigen Fall zu langsam.
 */
export function AnsprechpartnerModal({
  open, onClose, emailId, tenantSlug, prefill, onSuccess,
}: {
  open: boolean
  onClose: () => void
  emailId: number
  tenantSlug: string
  prefill?: { salutation?: string; first?: string; last?: string; phone?: string; email?: string }
  onSuccess: () => void
}) {
  const [suche, setSuche] = useState("")
  const [treffer, setTreffer] = useState<HeroContactHit[]>([])
  const [sucht, setSucht] = useState(false)
  const [gesucht, setGesucht] = useState(false)
  const [kunde, setKunde] = useState<HeroContactHit | null>(null)
  const [salutation, setSalutation] = useState(prefill?.salutation || "Frau")
  const [first, setFirst] = useState(prefill?.first || "")
  const [last, setLast] = useState(prefill?.last || "")
  const [phone, setPhone] = useState(prefill?.phone || "")
  // Rechnungsempfaenger: HERO zieht dann die Anschrift DIESES Kontakts ins
  // Adressfeld des Belegs — der Fall "Mieter ist Ansprechpartner, Rechnung
  // geht an den Vermieter" (Ticket #2).
  const [rechnungsempfaenger, setRechnungsempfaenger] = useState(false)
  const [mail, setMail] = useState(prefill?.email || "")
  // #144 (Marlene, 30.09.2026): „Wir brauchen hier unbedingt noch das Feld:
  // Position/Funktion" — HERO fuehrt es am Kontakt als `position`.
  const [position, setPosition] = useState("")
  const [err, setErr] = useState<string | null>(null)

  // Beim Öffnen aus der Mail neu vorbelegen — sonst zeigt ein zweites Öffnen
  // noch die Daten der vorigen Mail.
  useEffect(() => {
    if (!open) return
    setSalutation(prefill?.salutation || "Frau")
    setFirst(prefill?.first || "")
    setLast(prefill?.last || "")
    setPhone(prefill?.phone || "")
    setRechnungsempfaenger(false)
    setMail(prefill?.email || "")
    setPosition("")
    setKunde(null); setSuche(""); setTreffer([]); setErr(null); setGesucht(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, emailId])

  const suchen = async () => {
    if (!suche.trim()) return
    setSucht(true)
    setErr(null)
    try {
      setTreffer(await searchHeroContacts(tenantSlug, suche))
      setGesucht(true)
    } catch (e) {
      // Kein stiller Nulltreffer: „keine Treffer" und „Suche kaputt" sehen
      // sonst gleich aus, und der Nutzer legt den Kunden neu an (#52).
      setTreffer([])
      setGesucht(false)
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setSucht(false)
    }
  }

  const anlegen = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/v1/emails/${emailId}/hero-ansprechpartner`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          parent_customer_id: kunde?.id,
          salutation, first_name: first.trim(), last_name: last.trim(),
          phone: phone.trim(), email: mail.trim(), position: position.trim(),
          is_invoice_recipient: rechnungsempfaenger,
        }),
      })
      const json = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`)
      return json
    },
    onSuccess: () => {
      toast("✓ Ansprechpartner:in angelegt und mit der Mail verknüpft")
      onSuccess()
      onClose()
    },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  if (!open) return null
  const bereit = !!kunde && !!(first.trim() || last.trim())

  // 🔴 Als echtes Overlay ueber `createPortal` — genau wie CreateContactModal.
  // Die erste Fassung nutzte die Klassen `ev2-modal-backdrop`/`ev2-modal` aus
  // der alten Admin-Oberflaeche. Die gibt es im React-Portal nicht, also fiel
  // der Dialog auf normalen Fluss zurueck und erschien UNTER der E-Mail statt
  // darueber (gemeldet 17.08.2026).
  return createPortal(
    <div
      className="pp-overlay oben"
      data-portal-modal
      style={{
        position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "24px 16px", overflowY: "auto", backdropFilter: "blur(4px)",
      }}
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
    >
      <div
        onClick={(ev) => ev.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12, maxWidth: 560, width: "100%",
          boxShadow: "0 20px 60px rgba(0,0,0,.2), 0 0 0 1px rgba(0,0,0,.05)",
          margin: "auto",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 20px 0",
        }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            Als Ansprechpartner:in anlegen
          </h3>
          <button type="button" className="ev2-hero-ibtn" onClick={onClose} title="Schliessen">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div style={{ padding: 20 }}>
          <p style={{ fontSize: 12.5, color: "var(--text-secondary)", marginBottom: 12 }}>
            Für Mieter, Hausverwaltungen und Ansprechpartner vor Ort: Der Kontakt
            hängt unter dem Kunden, statt selbst einer zu werden.
          </p>

          {/* 1. Der Kunde, unter dem die Person hängt */}
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>
            1 · Kunde (Eigentümer, Verwaltung, Auftraggeber)
          </div>
          {kunde ? (
            <div style={{
              display: "flex", alignItems: "center", gap: 8, padding: "6px 8px",
              border: "1px solid var(--border)", borderRadius: 6, marginBottom: 12,
            }}>
              <span style={{ fontSize: 13 }}>
                <strong>{kontaktName(kunde)}</strong>
                {kunde.address && (
                  <span style={{ color: "var(--text-muted)" }}> · {kunde.address}</span>
                )}
              </span>
              <button className="ev2-hero-ibtn" style={{ marginLeft: "auto" }}
                      onClick={() => setKunde(null)}>Ändern</button>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <input
                  value={suche}
                  onChange={(e) => setSuche(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") suchen() }}
                  placeholder="Name des Kunden…"
                  style={{ flex: 1 }}
                />
                <button className="ev2-hero-ibtn" onClick={suchen} disabled={sucht}>
                  <Search className="w-3.5 h-3.5" /> {sucht ? "…" : "Suchen"}
                </button>
              </div>
              {treffer.length > 0 && (
                <div style={{ maxHeight: 160, overflowY: "auto", marginBottom: 12 }}>
                  {treffer.map((t) => (
                    <div
                      key={t.id}
                      onClick={() => setKunde(t)}
                      style={{
                        padding: "5px 8px", cursor: "pointer", fontSize: 12.5,
                        borderBottom: "1px solid var(--border)",
                      }}
                    >
                      <strong>{kontaktName(t)}</strong>
                      {[kontaktFirmaZusatz(t), t.address].filter(Boolean).length > 0 && (
                        <span style={{ color: "var(--text-muted)" }}>
                          {" · "}{[kontaktFirmaZusatz(t), t.address].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {gesucht && treffer.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
                  Keine Treffer für „{suche.trim()}".
                </div>
              )}
            </>
          )}

          {/* 2. Die Person selbst — aus der Mail vorbelegt */}
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>
            2 · Ansprechpartner:in
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
            <label style={{ fontSize: 12 }}>Anrede
              <select value={salutation} onChange={(e) => setSalutation(e.target.value)}>
                <option value="Frau">Frau</option>
                <option value="Herr">Herr</option>
                <option value="Divers">Divers</option>
              </select>
            </label>
            <label style={{ fontSize: 12 }}>Position/Funktion
              <input value={position} onChange={(e) => setPosition(e.target.value)}
                     placeholder="z. B. Hausverwaltung, Mieterin, Hausmeister" maxLength={120} />
            </label>
            <label style={{ fontSize: 12 }}>Vorname
              <input value={first} onChange={(e) => setFirst(e.target.value)} />
            </label>
            <label style={{ fontSize: 12 }}>Nachname
              <input value={last} onChange={(e) => setLast(e.target.value)} />
            </label>
            <label style={{ fontSize: 12 }}>Telefon
              <input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </label>
            <label style={{ fontSize: 12 }}>E-Mail
              <input type="email" value={mail} onChange={(e) => setMail(e.target.value)} />
            </label>
          </div>

          <label style={{
            display: "flex", gap: 8, alignItems: "flex-start",
            fontSize: 12, margin: "10px 0 4px", cursor: "pointer",
          }}>
            <input
              type="checkbox"
              checked={rechnungsempfaenger}
              onChange={(e) => setRechnungsempfaenger(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              <strong>Rechnungsempfänger</strong>
              {/* 🔴 Der alte Text sagte „Rechnungen gehen an die Anschrift dieser
                  Person — für den Fall, dass der Ansprechpartner nicht zahlt".
                  Das war in sich verdreht: das Häkchen macht genau den
                  Ansprechpartner zum Zahler. Dazu kamen fünf Begriffe für zwei
                  Rollen („dieser Person", „der Ansprechpartner", „Mieter",
                  „Vermieter", „Hauptkontakt"), während der Dialog daneben
                  schlicht 1 und 2 durchnummeriert (#51, gemeldet 26.08.2026).
                  Deshalb hier nur noch die beiden Nummern von oben — und beide
                  Zustände des Häkchens, nicht nur einer. */}
              <span style={{ display: "block", color: "var(--text-muted)", marginTop: 2, lineHeight: 1.45 }}>
                Ohne Haken geht die Rechnung an <strong>1&nbsp;· Kunde</strong>.
                Mit Haken geht sie an die Anschrift von{" "}
                <strong>2&nbsp;· Ansprechpartner:in</strong> — für den Fall, dass
                jemand anderes als der Kunde zahlt. Die <em>Anrede</em> im Beleg
                bleibt in beiden Fällen der Kunde.
              </span>
            </span>
          </label>

          {err && (
            <div style={{ fontSize: 12, color: "var(--pp-danger, #c0392b)", marginBottom: 8 }}>{err}</div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="ev2-hero-ibtn" onClick={onClose}>Abbrechen</button>
            <button
              className="ev2-hero-ibtn ev2-hero-ibtn-primary"
              disabled={!bereit || anlegen.isPending}
              onClick={() => { setErr(null); anlegen.mutate() }}
              title={bereit ? "" : "Kunde und Name werden gebraucht"}
            >
              <UserPlus className="w-3.5 h-3.5" />
              {anlegen.isPending ? "Legt an…" : "Anlegen"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
